import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface UnsubscribeData {
  subscription_count: number;
  subscription_details: {
    games: number;
    forum_topics: number;
    forum_posts: number;
    messages: number;
    game_downloads: number;
  };
  unsubscribe_token: {
    expires_in_hours: number;
    expires_at: string;
  };
}

type SubscriptionType = 'games' | 'forum_topics' | 'forum_posts' | 'messages' | 'game_downloads';

const UnsubscribePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<UnsubscribeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unsubscribing, setUnsubscribing] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<Set<SubscriptionType> | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    fetch(`/unsubscribe/${token}`, {
      headers: { 'Accept': 'application/json' }
    })
      .then(res => {
        if (!res.ok) throw new Error('A leiratkozási link lejárt vagy érvénytelen.');
        return res.json();
      })
      .then(jsonData => {
        setData(jsonData);
        const initialSelection = new Set<SubscriptionType>();
        if (jsonData.subscription_details.games > 0) initialSelection.add('games');
        if (jsonData.subscription_details.forum_topics > 0) initialSelection.add('forum_topics');
        if (jsonData.subscription_details.forum_posts > 0) initialSelection.add('forum_posts');
        if (jsonData.subscription_details.messages > 0) initialSelection.add('messages');
        if (jsonData.subscription_details.game_downloads > 0) initialSelection.add('game_downloads');
        setSelectedTypes(initialSelection);
        setError(null);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Hiba a betöltésnél');
        setData(null);
        setSelectedTypes(new Set<SubscriptionType>());
      })
      .finally(() => setLoading(false));
  }, [token]);

  const toggleType = (type: SubscriptionType) => {
    const newSelected = new Set<SubscriptionType>(selectedTypes || new Set<SubscriptionType>());
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelectedTypes(newSelected);
  };

  const handleUnsubscribe = async () => {
    if (!token || !selectedTypes || selectedTypes.size === 0) return;

    setUnsubscribing(true);

    try {
      const response = await fetch(`/unsubscribe/${token}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ types: Array.from(selectedTypes) })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Leiratkozási hiba');
      }

      const result = await response.json();
      setSuccessMessage(result.message || 'Sikeresen leiratkozott!');
      setError(null);

      if (data) {
        const updatedData = { ...data };
        let deletedCount = 0;
        selectedTypes.forEach(type => {
          if (type === 'games') {
            deletedCount += updatedData.subscription_details.games;
            updatedData.subscription_details.games = 0;
          }
          if (type === 'forum_topics') {
            deletedCount += updatedData.subscription_details.forum_topics;
            updatedData.subscription_details.forum_topics = 0;
          }
          if (type === 'forum_posts') {
            deletedCount += updatedData.subscription_details.forum_posts;
            updatedData.subscription_details.forum_posts = 0;
          }
          if (type === 'messages') {
            deletedCount += updatedData.subscription_details.messages;
            updatedData.subscription_details.messages = 0;
          }
          if (type === 'game_downloads') {
            deletedCount += updatedData.subscription_details.game_downloads;
            updatedData.subscription_details.game_downloads = 0;
          }
        });
        updatedData.subscription_count -= deletedCount;
        setData(updatedData);
        setSelectedTypes(new Set());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hiba a leiratkozásnál');
    } finally {
      setUnsubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <p className="text-center text-gray-600">Betöltés...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className={`p-4 rounded-md ${successMessage ? 'bg-green-50 border-l-4 border-green-500' : 'bg-red-50 border-l-4 border-red-500'} mb-6`}>
            <p className={`${successMessage ? 'text-green-800' : 'text-red-800'} font-semibold`}>{error}</p>
          </div>
          <a href="/" className="block text-center bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded">
            ← Vissza a főoldalra
          </a>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <p className="text-red-800">A leiratkozási link lejárt vagy érvénytelen.</p>
          </div>
          <a href="/" className="block text-center bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded">
            ← Vissza a főoldalra
          </a>
        </div>
      </div>
    );
  }

  const { subscription_count, subscription_details, unsubscribe_token } = data;

  if (subscription_count === 0) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold mb-4 text-gray-800">Leiratkozás az értesítésekről</h1>

          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
            <h2 className="text-xl font-bold text-green-900 mb-2">✓ Nincs aktív feliratkozás</h2>
            <p className="text-green-800">
              Már leiratkozott az összes értesítésről. Jelenleg nem kap értesítéseket.
            </p>
            <p className="text-sm text-green-700 mt-2">
              Ez a link <strong>{unsubscribe_token.expires_in_hours} óráig</strong> még érvényes.
            </p>
          </div>

          <a href="/" className="block text-center bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded">
            ← Vissza a főoldalra
          </a>
        </div>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
            <h2 className="text-xl font-bold text-green-900 mb-2">✓ Sikeres leiratkozás</h2>
            <p className="text-green-800">{successMessage}</p>
          </div>
          <a href="/" className="block text-center bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded">
            ← Vissza a főoldalra
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold mb-4 text-gray-800">
          Leiratkozás az értesítésekről
        </h1>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <p className="text-gray-700 mb-2">
            <strong>Jelenleg {subscription_count} aktív feliratkozás</strong> található.
          </p>
          <p className="text-sm text-gray-600">
            Ez a link <strong>{unsubscribe_token.expires_in_hours} óráig</strong> érvényes
            ({new Date(unsubscribe_token.expires_at).toLocaleString('hu-HU')}-ig).
          </p>
        </div>

        <div className="bg-gray-50 rounded p-4 mb-6">
          <h3 className="font-bold text-gray-800 mb-4">Válassz a feliratkozásaid közül:</h3>
          <div className="space-y-3">
            {subscription_details.games > 0 && (
              <label className="flex items-center p-3 border border-gray-200 rounded hover:bg-blue-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTypes?.has('games') ?? false}
                  onChange={() => toggleType('games')}
                  className="w-5 h-5 text-blue-600 rounded"
                />
                <div className="ml-3 flex-1">
                  <span className="font-semibold text-gray-800">Játék megjegyzések</span>
                  <p className="text-sm text-gray-600">{subscription_details.games} követett játék</p>
                </div>
                <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                  {subscription_details.games}
                </span>
              </label>
            )}

            {subscription_details.forum_topics > 0 && (
              <label className="flex items-center p-3 border border-gray-200 rounded hover:bg-purple-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTypes?.has('forum_topics') ?? false}
                  onChange={() => toggleType('forum_topics')}
                  className="w-5 h-5 text-purple-600 rounded"
                />
                <div className="ml-3 flex-1">
                  <span className="font-semibold text-gray-800">Fórum témakörök</span>
                  <p className="text-sm text-gray-600">{subscription_details.forum_topics} követett témakör</p>
                </div>
                <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                  {subscription_details.forum_topics}
                </span>
              </label>
            )}

            {subscription_details.forum_posts > 0 && (
              <label className="flex items-center p-3 border border-gray-200 rounded hover:bg-pink-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTypes?.has('forum_posts') ?? false}
                  onChange={() => toggleType('forum_posts')}
                  className="w-5 h-5 text-pink-600 rounded"
                />
                <div className="ml-3 flex-1">
                  <span className="font-semibold text-gray-800">Fórum válaszok</span>
                  <p className="text-sm text-gray-600">{subscription_details.forum_posts} követett hozzászólás</p>
                </div>
                <span className="bg-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                  {subscription_details.forum_posts}
                </span>
              </label>
            )}

            {subscription_details.messages > 0 && (
              <label className="flex items-center p-3 border border-gray-200 rounded hover:bg-orange-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTypes?.has('messages') ?? false}
                  onChange={() => toggleType('messages')}
                  className="w-5 h-5 text-orange-600 rounded"
                />
                <div className="ml-3 flex-1">
                  <span className="font-semibold text-gray-800">Üzenet értesítések</span>
                  <p className="text-sm text-gray-600">{subscription_details.messages} követett beszélgetés</p>
                </div>
                <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                  {subscription_details.messages}
                </span>
              </label>
            )}

            {subscription_details.game_downloads > 0 && (
              <label className="flex items-center p-3 border border-gray-200 rounded hover:bg-green-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTypes?.has('game_downloads') ?? false}
                  onChange={() => toggleType('game_downloads')}
                  className="w-5 h-5 text-green-600 rounded"
                />
                <div className="ml-3 flex-1">
                  <span className="font-semibold text-gray-800">Játék letöltés értesítések</span>
                  <p className="text-sm text-gray-600">{subscription_details.game_downloads} követett játék</p>
                </div>
                <span className="bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                  {subscription_details.game_downloads}
                </span>
              </label>
            )}
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-6">
          <h3 className="font-bold text-yellow-900 mb-2">⚠️ Fontos - később bármikor visszaíratkozhatsz!</h3>
          <ul className="text-yellow-900 text-sm space-y-1 list-disc list-inside">
            <li>A kiválasztott {(selectedTypes?.size ?? 0) > 0 ? (selectedTypes?.size ?? 0) : 0} feliratkozás <strong>véglegesen törlődik</strong></li>
          </ul>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleUnsubscribe}
            disabled={unsubscribing || (selectedTypes?.size ?? 0) === 0}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold py-3 px-4 rounded transition"
          >
            {unsubscribing ? 'Feldolgozás...' : (selectedTypes?.size ?? 0) === 0 ? 'Válassz legalább egyet!' : `✓ Leiratkozom (${selectedTypes?.size ?? 0})`}
          </button>

          <a href="/" className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded text-center transition">
            ← Mégse, visszamegyek
          </a>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
